import React, { useEffect, useRef, useState } from "react";
import { CallAiActions } from "@/components/ai/CallAiActions";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

export interface OutcomeTarget {
  callId: string;
  label: string;
  /** Company and number, shown under the name */
  sub?: string;
}

type CallState = { call_status: string; answered: boolean; settled: boolean; duration_sec: number | null; outcome: string | null };

const OPTIONS: { key: string; label: string; needsAnswer?: boolean; noAnswer?: boolean; dot: string }[] = [
  { key: "INTERESTED", label: "Interested", needsAnswer: true, dot: "bg-success" },
  { key: "CALL_BACK", label: "Call back later", dot: "bg-warning" },
  { key: "NOT_INTERESTED", label: "Not interested", needsAnswer: true, dot: "bg-destructive" },
  { key: "NO_ANSWER", label: "No answer", noAnswer: true, dot: "bg-muted-foreground" },
  { key: "BUSY", label: "Busy", noAnswer: true, dot: "bg-muted-foreground" },
  { key: "VOICEMAIL", label: "Voicemail", dot: "bg-muted-foreground" },
  { key: "WRONG_NUMBER", label: "Wrong number", needsAnswer: true, dot: "bg-destructive" },
  { key: "DO_NOT_CALL", label: "Asked not to call", needsAnswer: true, dot: "bg-destructive" },
];

const mmss = (s: number) => `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s`;
/** "yyyy-MM-ddTHH:mm" in the browser's own time, for the date box; tomorrow 11:00 unless told otherwise */
const localStamp = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
const tomorrowAtEleven = () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(11, 0, 0, 0); return localStamp(d); };

/**
 * Opens when a call ends: the rep says how it went, adds a note, and for "call back later" picks a time. The CRM turns
 * that into a Call entry on the record and a follow-up task. Duration and whether anyone answered come from Plivo,
 * and the choices that contradict that are greyed out.
 */
export function CallOutcomeDialog({ target, onClose }: { target: OutcomeTarget | null; onClose: (saved: boolean) => void }) {
  const { success, error } = useToast();
  const [state, setState] = useState<CallState | null>(null);
  const [outcome, setOutcome] = useState<string>("");
  const [note, setNote] = useState("");
  const [callbackAt, setCallbackAt] = useState(tomorrowAtEleven());
  const [saving, setSaving] = useState(false);
  const chosenByRep = useRef(false);

  // Plivo's reports arrive a few seconds after the call; ask until they have (or give up after about 20 seconds)
  useEffect(() => {
    if (!target) return;
    setState(null); setOutcome(""); setNote(""); setCallbackAt(tomorrowAtEleven()); chosenByRep.current = false;
    let alive = true;
    let tries = 0;
    const poll = async () => {
      try {
        const s = unwrapObject<CallState>(await api.get(`/api/Call/Log/${target.callId}`));
        if (!alive) return;
        setState(s);
        // Suggest the obvious outcome for a call nobody answered, unless the rep already chose
        if (!chosenByRep.current && !s.answered && s.settled) setOutcome(s.call_status === "BUSY" ? "BUSY" : "NO_ANSWER");
        if (!s.settled && ++tries < 10) setTimeout(poll, 2000);
      } catch {
        if (alive && ++tries < 3) setTimeout(poll, 2000);
      }
    };
    void poll();
    return () => { alive = false; };
  }, [target?.callId]);

  const settled = !!state?.settled;
  const answered = !!state?.answered;
  const disabled = (o: (typeof OPTIONS)[number]) => settled && ((!answered && o.needsAnswer) || (answered && o.noAnswer));

  const save = async () => {
    if (!target || !outcome) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = { outcome };
      if (note.trim()) body.note = note.trim();
      if (outcome === "CALL_BACK" && callbackAt) body.callback_at = new Date(callbackAt).toISOString();
      const r = unwrapObject<{ task_created: boolean }>(await api.post(`/api/Call/Outcome/${target.callId}`, body));
      success("Call saved", r.task_created ? "It's on the timeline, and a follow-up task is set." : "It's on the timeline.");
      // Open timelines reload now, and again shortly after in case the recording arrives late
      for (const ms of [0, 20000, 60000]) setTimeout(() => window.dispatchEvent(new Event("crm:call-saved")), ms);
      onClose(true);
    } catch (err: any) {
      error("Couldn't save the call", err?.message || "Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && !saving && onClose(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>How did the call with {target?.label} go?</DialogTitle>
          <DialogDescription>
            {target?.sub ? `${target.sub} · ` : ""}
            {!state ? "Checking the call…" : !settled ? "Waiting for the call report…" : answered ? `Answered · ${state.duration_sec !== null ? mmss(state.duration_sec) : "length coming"}` : "Not answered"}
          </DialogDescription>
        </DialogHeader>

        <div role="radiogroup" aria-label="How the call went" className="grid grid-cols-2 gap-1.5">
          {OPTIONS.map((o) => {
            const off = disabled(o);
            const on = outcome === o.key;
            return (
              <button
                key={o.key}
                type="button"
                role="radio"
                aria-checked={on}
                disabled={off}
                title={off ? (answered ? "This call was answered." : "Nobody answered this call.") : undefined}
                onClick={() => { chosenByRep.current = true; setOutcome(o.key); }}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${on ? "border-primary bg-accent font-semibold text-accent-foreground" : "bg-card hover:bg-muted"}`}
              >
                <span className={`size-2 shrink-0 rounded-full ${o.dot}`} />
                {o.label}
              </button>
            );
          })}
        </div>

        {outcome === "CALL_BACK" && (
          <div className="space-y-1.5">
            <Label htmlFor="call-callback-at">Call back at</Label>
            <Input id="call-callback-at" type="datetime-local" value={callbackAt} min={localStamp(new Date())} onChange={(e) => setCallbackAt(e.target.value)} />
            <p className="text-xs text-muted-foreground">A follow-up task is created for this time. Clear it to skip the task.</p>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="call-note">Note (optional)</Label>
          <Textarea id="call-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3} placeholder="What was said or agreed" />
        </div>

        {target && answered && (
          <CallAiActions
            callId={target.callId}
            variant="outcome"
            waitForRecording
            onUse={(s) => {
              const pick = OPTIONS.find((o) => o.key === s.suggested_outcome);
              if (pick && !disabled(pick)) {
                chosenByRep.current = true;
                setOutcome(pick.key);
              }
              setNote([s.summary, s.next_step ? `Next: ${s.next_step}` : ""].filter(Boolean).join("\n").slice(0, 2000));
              if (s.follow_up_in_days) {
                const d = new Date();
                d.setDate(d.getDate() + s.follow_up_in_days);
                d.setHours(11, 0, 0, 0);
                setCallbackAt(localStamp(d));
              }
            }}
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} disabled={saving}>Later</Button>
          <Button onClick={save} disabled={saving || !outcome}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
